/**
 * Builds the `audio.input.transcription` object for an OpenAI Realtime
 * transcription session.
 *
 * This exists because the current-generation models and the legacy ones take
 * *incompatible* parameter shapes, and getting it wrong is not a soft failure —
 * the Realtime API rejects the session outright:
 *
 *   - gpt-live-transcribe / gpt-transcribe take `languages: string[]` and
 *     explicitly reject a payload that also carries the singular `language`.
 *     They additionally accept `prompt` and `keywords` for vocabulary biasing.
 *   - gpt-4o-transcribe / whisper-1 take the singular `language` and support
 *     neither `keywords` nor `languages`.
 *
 * We store the singular `language` as the canonical config shape (see
 * OpenAIRealtimeListenConfigSchema) and map it per-model here, so a row that
 * has not yet been migrated off a legacy model cannot be broken by the new
 * parameter shape.
 */

export const DEFAULT_REALTIME_STT_MODEL = 'gpt-live-transcribe';

/** Models that use the `languages` array and support vocabulary biasing. */
const CURRENT_GEN_MODELS = new Set(['gpt-live-transcribe', 'gpt-transcribe']);

export function supportsKeywordBiasing(model: string): boolean {
  return CURRENT_GEN_MODELS.has(model);
}

/**
 * Streaming latency/quality tradeoff for current-generation models.
 *
 * Per OpenAI: "Lower delay settings can produce earlier partial text. Higher
 * delay settings give the model more audio context before emitting text and
 * can improve word error rate."
 *
 * The API default is undocumented, which is precisely why we set it: an
 * unstated default silently chooses this tradeoff on our behalf, and a
 * quality-leaning default costs response time on every single turn.
 */
export type TranscriptionDelay = 'minimal' | 'low' | 'medium' | 'high' | 'xhigh';

/**
 * Favours responsiveness. Room conversation is turn-based and interactive, so
 * time-to-first-audio dominates perceived quality; the accuracy that a higher
 * delay would buy is better recovered with `keywords` biasing, which costs no
 * latency at all.
 */
export const DEFAULT_TRANSCRIPTION_DELAY: TranscriptionDelay = 'low';

export interface StoredTranscriptionConfig {
  model?: string;
  language?: string;
  prompt?: string;
  keywords?: string[];
  delay?: TranscriptionDelay;
}

export interface RealtimeTranscriptionConfig {
  model: string;
  language?: string;
  languages?: string[];
  prompt?: string;
  keywords?: string[];
  delay?: TranscriptionDelay;
}

/**
 * `keywords` entries must be single-line and free of `<`, `>`, CR and LF, or
 * the API rejects them. Callers assemble these from room data (team names,
 * roster surnames), so sanitise rather than trust.
 */
function sanitizeKeywords(keywords: string[]): string[] {
  const seen = new Set<string>();
  for (const raw of keywords) {
    if (typeof raw !== 'string') continue;
    const cleaned = raw.replace(/[<>\r\n]/g, ' ').replace(/\s+/g, ' ').trim();
    if (cleaned) seen.add(cleaned);
  }
  return [...seen];
}

export function buildTranscriptionConfig(
  stored: StoredTranscriptionConfig
): RealtimeTranscriptionConfig {
  const model = stored.model || DEFAULT_REALTIME_STT_MODEL;

  const config: RealtimeTranscriptionConfig = { model };

  if (supportsKeywordBiasing(model)) {
    // Current gen: `languages` array only — never send the singular field.
    if (stored.language) config.languages = [stored.language];
    if (stored.prompt) config.prompt = stored.prompt;
    if (stored.keywords?.length) {
      const keywords = sanitizeKeywords(stored.keywords);
      if (keywords.length) config.keywords = keywords;
    }
    // Always stated, never left to the undocumented API default.
    config.delay = stored.delay ?? DEFAULT_TRANSCRIPTION_DELAY;
  } else {
    // Legacy gen: singular `language`; `keywords` unsupported, so drop it
    // rather than let the API reject the session.
    if (stored.language) config.language = stored.language;
    if (stored.prompt) config.prompt = stored.prompt;
  }

  return config;
}

/**
 * Whether a model accepts `audio.input.turn_detection`.
 *
 * Current-generation transcription models do NOT. Sending it is not ignored --
 * the API rejects session creation outright with
 * "Turn detection is not supported for this transcription model"
 * (400, param session.audio.input.turn_detection). These models require the
 * caller to segment turns by sending `input_audio_buffer.commit` explicitly;
 * that commit is what produces `.completed`, and `.completed` is the only
 * source of a final transcript.
 *
 * Legacy models rely on server VAD, which they enable by default when the
 * field is omitted -- so we omit it for them too rather than restate it.
 */
export function supportsTurnDetection(model: string): boolean {
  return !CURRENT_GEN_MODELS.has(model);
}

/**
 * Whether this model requires the client to commit the input buffer itself.
 * The inverse of turn-detection support: exactly one of the two mechanisms
 * ends a turn, and picking the wrong one yields no final transcript at all.
 */
export function requiresExplicitCommit(model: string): boolean {
  return CURRENT_GEN_MODELS.has(model);
}
